import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
} from '@mui/material'
import { Preview as PreviewIcon } from '@mui/icons-material'

interface DigestEmailPreviewProps {
  open: boolean
  onClose: () => void
  nudgeMessage: string
  congratsMessage: string
  idleMessage: string
  weeklyGoal: string
}

const APP_NAME = 'MissFit Mindset Coaching Platform'

const SAMPLE_STALE_CONTACTS = [
  { name: 'Sarah Chen', company: 'Acme Corp' },
  { name: 'Mike Johnson', company: 'Google' },
  { name: 'Lisa Park', company: 'Meta' },
]

function buildPreviewHtml(variant: 'below_goal' | 'hit_goal', props: DigestEmailPreviewProps): string {
  const { nudgeMessage, congratsMessage, idleMessage, weeklyGoal } = props
  const goal = weeklyGoal || '5'
  const belowCount = '2'
  const hitCount = '6'

  // Match the real email's CTA URL construction (backend/lambdas/generate-weekly-summary
  // uses process.env.APP_URL). Using window.location.origin keeps the preview honest
  // across localhost, dev, and prod without an env-var lookup.
  const portalUrl = window.location.origin

  let title: string
  let message: string

  if (variant === 'hit_goal') {
    title = 'Great Week!'
    message = (congratsMessage || 'Great work! You logged {count} touchpoints last week, hitting your goal of {goal}!')
      .replace(/\{count\}/g, hitCount)
      .replace(/\{goal\}/g, goal)
  } else {
    title = 'Your Weekly Side Door Update'
    message = (nudgeMessage || 'You logged {count} networking touchpoints last week. Your goal is {goal}. Keep the momentum going!')
      .replace(/\{count\}/g, belowCount)
      .replace(/\{goal\}/g, goal)
  }

  // Add stale contacts section
  const idleTemplate = idleMessage || "You haven't connected with {contactName} in {days} days"
  const staleSection = SAMPLE_STALE_CONTACTS.map((c) => {
    const line = idleTemplate
      .replace(/\{contactName\}/g, c.name)
      .replace(/\{days\}/g, '12')
    return `- ${line}`
  }).join('\n')

  const fullMessage = message + '\n\nContacts that need your attention:\n' + staleSection

  // Replicate buildEmailTemplate from backend/layers/notifications/nodejs/notifications.mjs
  const messageHtml = fullMessage
    .split('\n')
    .map((line) => {
      if (line.startsWith('- ')) {
        return `<li style="color: #4a5568; line-height: 1.8; margin: 0;">${line.substring(2)}</li>`
      }
      if (line === '') return '<br/>'
      return `<p style="color: #4a5568; line-height: 1.6; margin: 0 0 8px;">${line}</p>`
    })
    .join('')

  // Wrap list items in a <ul>
  const wrappedHtml = messageHtml.replace(
    /(<li[^>]*>.*?<\/li>)+/g,
    (match) => `<ul style="padding-left: 20px; margin: 8px 0;">${match}</ul>`
  )

  return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="margin: 0; padding: 20px; background: #f7fafc; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
<div style="max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #2c5282 0%, #4a90a4 100%); padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
    <h1 style="color: #fff; margin: 0; font-size: 24px;">${APP_NAME}</h1>
  </div>
  <div style="padding: 30px; background: #fff; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px;">
    <h2 style="color: #2c5282; margin: 0 0 16px;">${title}</h2>
    ${wrappedHtml}
    <div style="text-align: center; margin: 24px 0;">
      <a href="${portalUrl}/portal/networking" target="_blank" rel="noopener noreferrer" style="display: inline-block; background-color: #2c5282; color: #fff; padding: 12px 28px; text-decoration: none; border-radius: 6px; font-weight: 600;">Log a Touchpoint</a>
    </div>
    <hr style="border: none; border-top: 1px solid #e2e8f0; margin: 24px 0;" />
    <p style="color: #a0aec0; font-size: 12px; text-align: center;">
      This notification was sent by ${APP_NAME}.
      <br><a href="${portalUrl}/portal" target="_blank" rel="noopener noreferrer" style="color: #2c5282; text-decoration: none;">Visit Dashboard</a>
    </p>
  </div>
</div>
</body>
</html>`.trim()
}

export default function DigestEmailPreview({ open, onClose, ...templateProps }: DigestEmailPreviewProps) {
  const [variant, setVariant] = useState<'below_goal' | 'hit_goal'>('below_goal')

  const html = useMemo(
    () => buildPreviewHtml(variant, { open, onClose, ...templateProps }),
    [variant, templateProps.nudgeMessage, templateProps.congratsMessage, templateProps.idleMessage, templateProps.weeklyGoal]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PreviewIcon />
        Email Preview
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Variant:
          </Typography>
          <ToggleButtonGroup
            value={variant}
            exclusive
            onChange={(_, v) => v && setVariant(v)}
            size="small"
          >
            <ToggleButton value="below_goal">Below Goal</ToggleButton>
            <ToggleButton value="hit_goal">Hit Goal</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f7fafc',
          }}
        >
          <iframe
            srcDoc={html}
            title="Email Preview"
            sandbox=""
            style={{ width: '100%', height: 500, border: 'none' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          This preview uses sample data. Actual emails will use real client data and contact names.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
